import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { SalesReport } from '../../interfaces/Report/SalesReport/SalesReport';
import {
  getDefaultSalesReportFilter,
  SalesReportFilter,
} from '../../interfaces/Report/SalesReport/SalesReportFilter';
import { getSalesReport } from '../../api/Report';
import { Pagination } from '../../interfaces/Pagination';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { OrderStatusDropdown } from '../../components/Dropdowns/OrderStatusDropdown';
import { SalesReportTable } from './SalesReportTable';
import { PeriodDropdown } from '../../components/Dropdowns/PeriodDropdown';
import { Period } from '../../interfaces/Period';

export const dateTimePickerStyle = {
  minWidth: 200,
  maxWidth: 300,
  flexGrow: 1,
  height: 'fit-content',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'none',
};

export const SalesReportPage = () => {
  const [report, setReport] = useState<PaginatedResponse<SalesReport>>({
    data: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(getDefaultSalesReportFilter());
  const [appliedPeriod, setAppliedPeriod] = useState<Period | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  const refreshReport = (filters?: SalesReportFilter) => {
    const appliedFilters = filters ?? filter;

    setLoading(true);
    getSalesReport(appliedFilters)
      .then((r) => {
        setReport(r);
        setAppliedPeriod(appliedFilters.period);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshReport();
  }, []);

  const handleSearch = () => {
    const newFilter = {
      ...filter,
      pagination: { ...filter.pagination, page: 0 },
    };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter = { ...filter, pagination: p };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Typography variant='h5' sx={{ ...sharedStyles.pageTitle }}>
        Satış Raporu
      </Typography>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Başlangıç'
                onChange={(newStartAt) =>
                  setFilter({ ...filter, startAt: newStartAt ?? undefined })
                }
                value={filter.startAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Bitiş'
                onChange={(newEndAt) =>
                  setFilter({ ...filter, endAt: newEndAt ?? undefined })
                }
                value={filter.endAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <OrderStatusDropdown
            label='Sipariş Durumu'
            value={filter.status}
            onChange={(newStatus) =>
              setFilter({ ...filter, status: newStatus })
            }
          />

          <PeriodDropdown
            label='Periyot'
            value={filter.period}
            onChange={(newPeriod) =>
              setFilter({ ...filter, period: newPeriod })
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() =>
                setFilter({
                  ...getDefaultSalesReportFilter(),
                  pagination: filter.pagination,
                })
              }
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtrele
            </Button>
          </Box>
        </Box>
      </Paper>

      <SalesReportTable
        rows={report.data}
        period={appliedPeriod}
        totalCount={report.totalCount}
        pagination={filter.pagination}
        onPagination={handlePagination}
      />
    </Box>
  );
};
